import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EcxTimePopup from './EcxTimePopup';
import { formatDisplayTime, normalizeTimeValue } from './ecxTimeUtils';

function EcxTimePicker({
    value = '',
    onChange,
    placeholder = 'Select time',
    disabled = false,
    clearable = false,
    size = 'sm',
    id,
    name,
    className = '',
    intervalMinutes = 5,
    minHour = 0,
    maxHour = 23,
}) {
    const generatedId = useId();
    const inputId = id || generatedId;
    const containerRef = useRef(null);
    const popupRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const normalizedValue = normalizeTimeValue(value);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleClickOutside = (event) => {
            const clickedInsideAnchor = containerRef.current?.contains(event.target);
            const clickedInsidePopup = popupRef.current?.contains(event.target);

            if (!clickedInsideAnchor && !clickedInsidePopup) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleClear = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onChange?.('');
        setIsOpen(false);
    };

    const toggleOpen = () => {
        if (disabled) {
            return;
        }

        setIsOpen((open) => !open);
    };

    const handleSelect = (nextValue) => {
        onChange?.(nextValue);
    };

    return (
        <div
            ref={containerRef}
            className={`ecx-time-picker ecx-time-picker--${size} ${
                normalizedValue ? 'has-value' : ''
            } ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className}`.trim()}
        >
            <span className="ecx-time-picker__icon" aria-hidden="true">
                <i className="bi bi-clock"></i>
            </span>

            <div className="ecx-time-picker__field">
                <button
                    type="button"
                    className="ecx-time-picker__open"
                    onClick={toggleOpen}
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-labelledby={`${inputId}-display`}
                >
                    <span
                        className={`ecx-time-picker__display ${normalizedValue ? '' : 'is-placeholder'}`}
                        id={`${inputId}-display`}
                    >
                        {normalizedValue ? formatDisplayTime(normalizedValue) : placeholder}
                    </span>
                </button>

                <div className="ecx-time-picker__actions">
                    {clearable && normalizedValue && !disabled && (
                        <button
                            type="button"
                            className="ecx-time-picker__clear"
                            onClick={handleClear}
                            aria-label="Clear time"
                            title="Clear time"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    )}
                    <button
                        type="button"
                        className="ecx-time-picker__trigger"
                        onClick={toggleOpen}
                        disabled={disabled}
                        aria-label="Open time picker"
                        title="Open time picker"
                    >
                        <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                </div>
            </div>

            {name && <input type="hidden" name={name} value={normalizedValue} />}

            {isOpen && !disabled && createPortal(
                <div ref={popupRef}>
                    <EcxTimePopup
                        value={normalizedValue}
                        onSelect={handleSelect}
                        onClose={() => setIsOpen(false)}
                        anchorRef={containerRef}
                        intervalMinutes={intervalMinutes}
                        minHour={minHour}
                        maxHour={maxHour}
                    />
                </div>,
                document.body
            )}
        </div>
    );
}

export default EcxTimePicker;
